/**
 * Echogram (Energy-Time-Curve) — mobile's light-themed Canvas2D counterpart
 * to web's `web/src/components/room/charts/echogramDraw.ts`. Same geometry
 * (`computeEchogramGeometry`) and same bounce-based fade constants, only the
 * ink palette differs — see `./_shared.ts`'s module doc for why the two
 * paint layers are intentionally separate (light card vs. dark dock).
 */
import type { ChartPad, PlotFrame } from '../layout';
import { computeEchogramGeometry, type EchogramPoint } from '../echogram';
import { setupCanvas, drawEmptyMessage, INK, withAlpha } from './_shared';

export const ECHOGRAM_PAD: ChartPad = { t: 10, r: 14, b: 22, l: 34 };

/** Bounce count at which the fade bottoms out — matches web's
 *  echogramDraw.ts exactly (a room's rays rarely get diagnostically
 *  interesting past this many bounces). */
const MAX_BOUNCE_FOR_FADE = 8;

export interface DrawEchogramOpts {
  distances: number[];
  energies: number[];
  bounces: number[];
  speakerIdx: number[];
  width: number;
  height: number;
  /** Time window to show, ms. Defaults to computeEchogramGeometry's own
   *  default (150ms). */
  maxTimeMs?: number;
}

function drawStem(ctx: CanvasRenderingContext2D, frame: PlotFrame, p: EchogramPoint): void {
  const x = frame.plotX + p.x;
  const yTop = frame.plotY + p.y;
  const yBase = frame.plotY + frame.plotH;
  const t = Math.min(1, p.bounces / MAX_BOUNCE_FOR_FADE);
  const opacity = 0.85 - t * 0.6;
  const radius = 2.4 - t * 1.6;

  ctx.strokeStyle = withAlpha(INK.primary, opacity * 0.6);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, yBase);
  ctx.lineTo(x, yTop);
  ctx.stroke();

  ctx.fillStyle = withAlpha(INK.primary, opacity);
  ctx.beginPath();
  ctx.arc(x, yTop, Math.max(0.6, radius), 0, Math.PI * 2);
  ctx.fill();
}

export function drawEchogramCanvas(canvas: HTMLCanvasElement, opts: DrawEchogramOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeEchogramGeometry({
    distances: opts.distances,
    energies: opts.energies,
    bounces: opts.bounces,
    speakerIdx: opts.speakerIdx,
    width: opts.width,
    height: opts.height,
    pad: ECHOGRAM_PAD,
    maxTimeMs: opts.maxTimeMs,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No ray arrivals yet');
    return;
  }

  ctx.textAlign = 'right';
  for (const t of geo.yTicks) {
    const y = geo.frame.plotY + t.y;
    ctx.strokeStyle = t.major ? INK.gridMajor : INK.gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(geo.frame.plotX, y);
    ctx.lineTo(geo.frame.plotX + geo.frame.plotW, y);
    ctx.stroke();
    ctx.fillStyle = INK.label;
    ctx.fillText(t.label, geo.frame.plotX - 4, y + 3);
  }
  ctx.textAlign = 'center';
  for (const t of geo.xTicks) {
    const x = geo.frame.plotX + t.x;
    ctx.strokeStyle = INK.gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, geo.frame.plotY);
    ctx.lineTo(x, geo.frame.plotY + geo.frame.plotH);
    ctx.stroke();
    ctx.fillStyle = INK.label;
    ctx.fillText(`${t.label}ms`, x, geo.frame.plotY + geo.frame.plotH + 12);
  }

  // Draw quietest-first so the loudest, most diagnostically relevant
  // arrivals paint on top of the late diffuse clutter (matches web's
  // echogramDraw.ts ordering).
  const ordered = [...geo.points].sort((a, b) => a.levelDb - b.levelDb);
  for (const p of ordered) drawStem(ctx, geo.frame, p);
}
